import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, ShoppingCart, Store as StoreIcon, PlusCircle, BadgeCheck } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import ProductImage from "@/components/ProductImage";

export default function SellerDashboard() {
    const { t, lang } = useLang();
    const [store, setStore] = useState(null);
    const [products, setProducts] = useState([]);
    const [orders, setOrders] = useState([]);
    const [payoutSummary, setPayoutSummary] = useState({
        pending: 0,
        available: 0,
        paid: 0,
    });

    useEffect(() => {
        api.get("/stores/mine").then((r) => setStore(r.data));
        api.get("/seller/products").then((r) => setProducts(r.data));
        api.get("/seller/orders").then((r) => setOrders(r.data));
        api.get("/seller/payouts/summary").then((r) => setPayoutSummary(r.data));
    }, []);

    const sales = orders.reduce(
        (sum, order) =>
            sum
            + Number(
                order.seller_revenue
                || 0
            ),
        0
    );

    const platformCommission =
        orders.reduce(
            (sum, order) =>
                sum
                + Number(
                    order.seller_revenue_commission
                    || 0
                ),
            0
        );

    const netDue = orders.reduce(
        (sum, order) =>
            sum
            + Number(
                order.seller_revenue_net
                || 0
            ),
        0
    );

    return (
        <Layout>
            <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
                <div className="flex items-end justify-between mb-8">
                    <div>
                        <span className="text-xs font-medium tracking-[0.2em] text-terracotta uppercase">
                            {lang === "ar" ? "أهلاً" : "Welcome"}
                        </span>
                        <h1 className="text-3xl md:text-4xl font-black text-espresso mt-1">{t("sellerDashboard")}</h1>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <Link to="/seller/reels"><Button variant="outline" className="rounded-full" data-testid="btn-my-reels">🎬 {t("myReels")}</Button></Link>
                        <Link to="/seller/verification"><Button variant="outline" className="rounded-full" data-testid="btn-verification"><BadgeCheck className="w-4 h-4 me-2" />{t("verification")}</Button></Link>
                        <Link to="/seller/store"><Button variant="outline" className="rounded-full" data-testid="btn-store-setup"><StoreIcon className="w-4 h-4 me-2" />{t("storeSetup")}</Button></Link>
                        <Link to="/seller/products/new"><Button className="rounded-full bg-terracotta text-white" data-testid="btn-add-product"><PlusCircle className="w-4 h-4 me-2" />{t("addProduct")}</Button></Link>
                    </div>
                </div>

                {!store && (
                    <div className="mb-6 bg-terracotta/10 border border-terracotta/30 rounded-2xl p-6" data-testid="store-warning">
                        <p className="text-espresso">
                            {lang === "ar" ? "لم تنشئ متجرك بعد. أنشئه لبدء البيع!" : "You haven't created your store yet."}
                        </p>
                        <Link to="/seller/store"><Button className="mt-3 rounded-full bg-terracotta text-white">{t("createStore")}</Button></Link>
                    </div>
                )}

                <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
                    <StatCard
                        label={t("myProducts")}
                        value={products.length}
                        icon={<Package className="w-5 h-5" />}
                    />

                    <StatCard
                        label={t("orders")}
                        value={orders.length}
                        icon={<ShoppingCart className="w-5 h-5" />}
                    />

                    <StatCard
                        label={
                            lang === "ar"
                                ? "\u0625\u062c\u0645\u0627\u0644\u064a \u0627\u0644\u0645\u0628\u064a\u0639\u0627\u062a"
                                : "Total sales"
                        }
                        value={`${sales.toFixed(2)} ${t("SAR")}`}
                        icon={<StoreIcon className="w-5 h-5" />}
                    />

                    <StatCard
                        label={
                            lang === "ar"
                                ? "\u0639\u0645\u0648\u0644\u0629 \u0627\u0644\u0645\u0646\u0635\u0629"
                                : "Platform commission"
                        }
                        value={`${platformCommission.toFixed(2)} ${t("SAR")}`}
                        icon={<StoreIcon className="w-5 h-5" />}
                    />

                    <StatCard
                        label={
                            lang === "ar"
                                ? "\u0635\u0627\u0641\u064a \u0627\u0644\u0645\u0628\u064a\u0639\u0627\u062a \u0628\u0639\u062f \u0627\u0644\u0639\u0645\u0648\u0644\u0629"
                                : "Net sales after commission"
                        }
                        value={`${netDue.toFixed(2)} ${t("SAR")}`}
                        icon={<StoreIcon className="w-5 h-5" />}
                    />
                </div>

                <div className="grid sm:grid-cols-3 gap-4 mb-10">

                    <StatCard
                        label={
                            lang === "ar"
                                ? "\u0645\u0633\u062a\u062d\u0642\u0627\u062a \u0645\u0639\u0644\u0642\u0629"
                                : "Pending payouts"
                        }
                        value={`${Number(payoutSummary.pending || 0).toFixed(2)} ${t("SAR")}`}
                        icon={<StoreIcon className="w-5 h-5" />}
                    />

                    <StatCard
                        label={
                            lang === "ar"
                                ? "\u0645\u062a\u0627\u062d \u0644\u0644\u062a\u062d\u0648\u064a\u0644"
                                : "Available for payout"
                        }
                        value={`${Number(payoutSummary.available || 0).toFixed(2)} ${t("SAR")}`}
                        icon={<StoreIcon className="w-5 h-5" />}
                    />

                    <StatCard
                        label={
                            lang === "ar"
                                ? "\u062a\u0645 \u062a\u062d\u0648\u064a\u0644\u0647"
                                : "Paid out"
                        }
                        value={`${Number(payoutSummary.paid || 0).toFixed(2)} ${t("SAR")}`}
                        icon={<StoreIcon className="w-5 h-5" />}
                    />

                </div>

                <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-white border border-border rounded-2xl p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="font-black text-espresso">{t("myProducts")}</h2>
                            <Link to="/seller/products" className="text-sm text-terracotta font-bold hover:underline">{t("seeAll")}</Link>
                        </div>
                        <div className="space-y-2">
                            {products.slice(0, 5).map((p) => (
                                <Link key={p.product_id} to={`/seller/products/${p.product_id}`} className="flex items-center gap-3 p-2 rounded-lg hover:bg-surface transition-colors" data-testid={`seller-product-${p.product_id}`}>
                                    <div className="w-10 h-10 rounded-lg bg-surface overflow-hidden">
                                        <ProductImage
                                            src={p.images?.[0]}
                                            alt={p.title}
                                            imageClassName="w-full h-full object-cover"
                                            showLabel={false}
                                        />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-bold text-sm truncate">{p.title}</div>
                                        <div className="text-xs text-muted-foreground">{t("stock")}: {p.stock}</div>
                                    </div>
                                    <div className="text-sm font-bold text-terracotta">{p.price} {t("SAR")}</div>
                                </Link>
                            ))}
                            {products.length === 0 && (
                                <div className="text-center text-sm text-muted-foreground py-4">{t("noResults")}</div>
                            )}
                        </div>
                    </div>

                    <div className="bg-white border border-border rounded-2xl p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="font-black text-espresso">{t("orders")}</h2>
                            <Link to="/seller/orders" className="text-sm text-terracotta font-bold hover:underline">{t("seeAll")}</Link>
                        </div>
                        <div className="space-y-2">
                            {orders.slice(0, 5).map((o) => (
                                <div key={o.order_id} className="p-2 rounded-lg hover:bg-surface flex items-center gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="font-bold text-sm truncate">#{o.order_id}</div>
                                        <div className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</div>
                                    </div>
                                    <div className="text-sm font-bold text-terracotta">{o.total.toFixed(2)}</div>
                                </div>
                            ))}
                            {orders.length === 0 && (
                                <div className="text-center text-sm text-muted-foreground py-4">{t("noResults")}</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}

function StatCard({ label, value, icon }) {
    return (
        <div className="bg-white border border-border rounded-2xl p-5" data-testid={`stat-${label}`}>
            <div className="flex items-center justify-between">
                <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
                <div className="text-terracotta">{icon}</div>
            </div>
            <div className="text-2xl font-black text-espresso mt-2">{value}</div>
        </div>
    );
}
